/**
 * Building transfer — import-time user resolution (pure).
 *
 * Applies the operator's per-referenced-user decision to the imported subtree:
 *  - copy  → emit an inert, NAME-ONLY `deleted:true` users/userList record
 *            (recognizable, cannot log in, no email/privileges).
 *  - remap → rewrite every UID reference (UID_SCALAR_FIELDS) from the source UID
 *            to an existing target-env UID; no user record written.
 *  - leave → nothing; the reference stays unresolved and the app renders
 *            "Okänd användare".
 *
 * The v1 UI only exposes "leave everything" and "remap everything to me", but
 * the full per-user strategy set is kept so a richer resolution UI needs no
 * format change.
 *
 * Known limitation: `buildings/{key}/externalContacts` is keyed by the external
 * user's UID, so remapping would rewrite the `externalUserId` field but not the
 * map key — moot here since externalContacts are excluded from the bundle.
 *
 * Ported from mf-functions branch `feat/building-transfer`
 * (`functions/src/buildings/transfer/applyUserResolution.ts`).
 */
import { UID_SCALAR_FIELDS, ExportedUsers, UserResolution } from './bundleFormat';

const UID_FIELD_SET: ReadonlySet<string> = new Set(UID_SCALAR_FIELDS);

export interface ResolutionContext {
    operatorUid: string;
    /** Stockholm `YYYY-MM-DD hh:mm:ss`. */
    now: string;
    /** Target-env UIDs that exist (deleted or not). A "copy" is skipped for these so a
     * live record is never clobbered by an inert stub. */
    existingTargetUids: ReadonlySet<string>;
    /** Target-env UIDs that exist AND are not deleted. Remap targets must be in this set —
     * remapping references onto a deleted (tombstone) user is rejected. */
    activeTargetUids: ReadonlySet<string>;
}

export interface ResolvedImport {
    building: unknown;
    summary: unknown;
    /** DB-path → value writes (e.g. `users/userList/{uid}`) to merge into the atomic update. */
    userRecordWrites: Record<string, unknown>;
}

/** Deep-clone a value, rewriting any UID-scalar field whose value is a remap source. */
function rewriteUids<T>(value: T, map: ReadonlyMap<string, string>): T {
    if (value === null || typeof value !== 'object') {
        return value;
    }
    if (Array.isArray(value)) {
        return value.map(item => rewriteUids(item, map)) as unknown as T;
    }
    const out: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
        if (UID_FIELD_SET.has(key) && typeof child === 'string' && map.has(child)) {
            out[key] = map.get(child);
        } else {
            out[key] = rewriteUids(child, map);
        }
    }
    return out as T;
}

export function applyUserResolution(
    building: unknown,
    summary: unknown,
    exportedUsers: ExportedUsers,
    resolution: UserResolution | undefined,
    ctx: ResolutionContext
): ResolvedImport {
    const userRecordWrites: Record<string, unknown> = {};
    const remap = new Map<string, string>();

    for (const [sourceUid, entry] of Object.entries(resolution ?? {})) {
        if (entry.strategy === 'copy') {
            // Never clobber a user that already exists in the target — the reference already
            // resolves there; writing an inert deleted stub would demote a live record.
            if (ctx.existingTargetUids.has(sourceUid)) {
                continue;
            }
            const name = exportedUsers[sourceUid]?.name;
            if (typeof name !== 'string') {
                // No name in the bundle — cannot write a meaningful record; leave dangling.
                continue;
            }
            userRecordWrites[`users/userList/${sourceUid}`] = {
                name,
                replacementName: name,
                deleted: true,
                deletedAt: ctx.now,
                deletedBy: ctx.operatorUid,
            };
        } else if (entry.strategy === 'remap') {
            const target = entry.targetUid;
            if (!target) {
                throw new Error(`Remap for ${sourceUid} is missing a target user`);
            }
            if (!ctx.activeTargetUids.has(target)) {
                throw new Error(
                    `Remap target ${target} does not exist in the target environment or is deleted`
                );
            }
            remap.set(sourceUid, target);
        }
        // "leave" → no record, no rewrite.
    }

    return {
        building: remap.size ? rewriteUids(building, remap) : building,
        summary: remap.size ? rewriteUids(summary, remap) : summary,
        userRecordWrites,
    };
}
